import { Controller } from '@hotwired/stimulus'

// The search is server-side, so typing waits for a pause instead of firing a
// request per keystroke.
const TYPING_PAUSE_MS = 300

export default class extends Controller {
  static targets = ['search']

  // A date typed into the search box is lifted into the Zeitraum filter, and
  // the server answers without that term. The box is permanent — the one thing
  // in the bar the server cannot re-render — so it is emptied here, or the
  // term would sit there contradicting the filter it became.
  //
  // It is emptied only once the caret has left it. Every complete date is also
  // the beginning of a longer one: "23.11." is lifted on its own, and is also
  // what someone halfway through "23.11.-23.12." has typed so far. Emptying
  // the box between two keystrokes would take the rest of that range with it.
  //
  // What was last sent is remembered on the permanent field itself rather than
  // on this controller, which is torn down and rebuilt on every redraw of the
  // bar. Only a term that was actually sent, has been left alone since, and is
  // not the search the server says it is answering, is cleared.
  connect() {
    // One frame later: Turbo puts a placeholder where a permanent element goes
    // and swaps the real node in only after the frame has rendered, so at
    // connect the search box is not in this form yet.
    this.pending = requestAnimationFrame(() => this.dropLiftedTerm())
  }

  disconnect() {
    clearTimeout(this.timeout)
    cancelAnimationFrame(this.pending)
  }

  dropLiftedTerm() {
    const field = this.searchField
    if (!field || field === document.activeElement) return
    if (field.dataset.settledTerm !== field.value) return

    const sent = field.dataset.submittedTerm
    if (!sent || field.value !== sent) return
    if ((this.element.dataset.searchValue || '') === field.value) return

    field.value = ''
    delete field.dataset.submittedTerm
    delete field.dataset.settledTerm
  }

  // Leaving the box is what settles the term: until then it is a term still
  // being typed, whatever it happens to parse as. The value is recorded rather
  // than a flag, so typing again un-settles it by itself.
  //
  // A term whose answer is still in flight is left to the next connect, when
  // the server's own account of what it searched for has arrived — read any
  // earlier, that account is the previous render's and would say the term was
  // lifted when it was not.
  settle() {
    const field = this.searchField
    if (!field) return

    field.dataset.settledTerm = field.value
    if (!this.awaitingAnswer) this.dropLiftedTerm()
  }

  get searchField() {
    return this.hasSearchTarget ? this.searchTarget : null
  }

  submitLater() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.send(), TYPING_PAUSE_MS)
  }

  send() {
    const field = this.searchField
    if (field) field.dataset.submittedTerm = field.value

    this.awaitingAnswer = true
    this.element.requestSubmit()
  }

  // "Filter zurücksetzen" navigates the card's frame back to the unfiltered
  // list, and the server renders every control in it empty again — every
  // control except the search box, which is permanent and therefore survives
  // the redraw still holding what was typed. That one is emptied here.
  clear() {
    clearTimeout(this.timeout)

    for (const field of this.element.elements) {
      if (field.name) field.value = ''
    }
  }

  // A GET form submits every field it has, so an untouched filter would ride
  // along in the URL as an empty param and end up in whatever link gets
  // shared. Disabling the empty ones leaves the URL saying only what was
  // actually chosen. This runs on the form's own submit event, ahead of
  // Turbo's document-level one, and Turbo reads the form synchronously there —
  // so the fields are back before the browser paints. The one being typed in
  // is left alone: disabling it would take the caret with it.
  //
  // A select is never left alone. It has no caret to lose, and it still has
  // focus right after "Alle" was picked in it — exempting it would put back
  // the very empty param that choice was meant to take out of the URL.
  dropBlankFields() {
    const blank = [...this.element.elements].filter(
      (field) => field.name && field.value === '' &&
        (field.tagName === 'SELECT' || field !== document.activeElement)
    )

    blank.forEach((field) => { field.disabled = true })
    setTimeout(() => blank.forEach((field) => { field.disabled = false }))
  }
}
